import React, { useState, useEffect } from 'react';
import { UserCircle, LogOut, FileText, Settings, ToggleLeft, ToggleRight, User, Eye, X as XIcon, Search } from 'lucide-react';
import Sidebar from './Sidebar';
import StructureSelector from './StructureSelector';
import StockAlerts from './StockAlerts';
import { VegaButton, VegaPanel, useVegaSummary } from './VegaWidget';
import { NovaProvider, NovaButton, NovaDialog } from './NovaWidget';
import GlobalSearchModal from './GlobalSearchModal';
import { useStructure } from '../contexts/StructureContext';
import { usePersonContext } from '../contexts/PersonContext';
import { useImpersonate } from '../contexts/ImpersonateContext';
import { getAppAgentPublicSettings } from '../api';

const viewTitles = {
  stocks: 'Gestion de Stocks',
  orders: 'Commandes',
  milk: 'Traçabilité Lait',
  medications: 'Traçabilité Médicaments',
  water: "Traçabilité Eau",
  equipment: 'Équipements',
  maintenance: 'Maintenance & Entretien',
  workOrders: 'Travaux',
  tasks: 'Tâches',
  financial: 'Dashboard Financier',
  dashboard: 'Tableau de bord',
  admin: 'Administration',
  hr: 'Ressources Humaines',
};

const AppLayout = ({
  currentView,
  onNavigate,
  user,
  onLogout,
  onShowChangelog,
  onShowSettings,
  children,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { structures } = useStructure();
  const { people, currentPerson, setCurrentPerson } = usePersonContext();
  const { isImpersonating, impersonatedUser, stopImpersonating } = useImpersonate();
  const isAdmin = user?.role === 'admin';
  const [novaPublic, setNovaPublic] = useState(false);
  const [vegaOpen, setVegaOpen] = useState(false);
  const { summary: vegaSummary, refresh: refreshVega } = useVegaSummary(isAdmin);

  // Multi-structure toggle (lifted from StockManagementApp for stocks view)
  // This state is passed down to StockManagementApp via props
  const [showAllStructures, setShowAllStructures] = useState(false);

  // Recherche globale Ctrl+K / Cmd+K
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);

  useEffect(() => {
    getAppAgentPublicSettings()
      .then(res => setNovaPublic(res.data?.novaPublic ?? false))
      .catch(() => {});
  }, []);

  // Raccourci clavier Ctrl+K / Cmd+K — actif partout dans l'app
  useEffect(() => {
    const handler = (e) => {
      // Ne pas intercepter si on est dans un input/textarea/contenteditable (sauf si modal est ouverte, dans ce cas pas de souci)
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setGlobalSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Navigation depuis un résultat de la recherche globale
  const handleGlobalSearchNavigate = (item) => {
    switch (item.entityType) {
      case 'employee':    onNavigate?.('hr',          { employeeId: item.entityId }); break;
      case 'child':       onNavigate?.('creche',      { childId: item.entityId }); break;
      case 'application': onNavigate?.('recruitment', { applicationId: item.entityId }); break;
      case 'job_offer':   onNavigate?.('recruitment', { offerId: item.entityId }); break;
      case 'product':     onNavigate?.('stocks',      { productId: item.entityId }); break;
      case 'supplier':    onNavigate?.('stocks',      { supplierId: item.entityId }); break;
      case 'equipment':   onNavigate?.('equipment',   { equipmentId: item.entityId }); break;
      case 'work_order':  onNavigate?.('workOrders',  { workOrderId: item.entityId }); break;
      case 'task':        onNavigate?.('tasks',       { taskId: item.entityId }); break;
      default: break;
    }
  };

  const showNova = isAdmin || novaPublic;

  return (
    <NovaProvider>
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        currentView={currentView}
        onNavigate={onNavigate}
        userRole={user?.role}
        userModules={user?.modules}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Banniere impersonation */}
        {isImpersonating && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm font-medium flex-shrink-0 z-50">
            <div className="flex items-center gap-2">
              <Eye size={16} />
              <span>
                Vue en tant que <strong>{impersonatedUser._impersonatedName}</strong>
                {' '}— role : {impersonatedUser._impersonatedRole}
                {' '}— modules : {Array.isArray(impersonatedUser.modules) ? impersonatedUser.modules.join(', ') : 'aucun'}
              </span>
            </div>
            <button
              onClick={stopImpersonating}
              className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg transition text-sm font-semibold">
              <XIcon size={14} />
              Revenir a mon compte
            </button>
          </div>
        )}
        {/* Header */}
        <header className="bg-primary text-white shadow-md flex-shrink-0">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Left: view title (with left padding on mobile for hamburger) */}
            <h1 className="text-lg font-bold truncate pl-10 lg:pl-0">
              {viewTitles[currentView] || 'SuiviPlus'}
            </h1>

            {/* Right: controls */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Recherche globale Ctrl+K */}
              <button
                onClick={() => setGlobalSearchOpen(true)}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-full border border-white/30 text-white/70 hover:text-white hover:border-white/60 transition text-xs sm:text-sm"
                title="Recherche globale (Ctrl+K)"
              >
                <Search size={14} />
                <span className="hidden md:inline">Rechercher</span>
                <kbd className="hidden lg:inline-flex items-center px-1 py-0.5 text-[10px] border border-white/30 rounded">⌘K</kbd>
              </button>

              {/* Nova — Orchestrateur IA */}
              {showNova && <NovaButton currentView={currentView} />}

              {/* Structure selector */}
              <div className="hidden sm:block">
                <StructureSelector />
              </div>

              {/* Multi-structure toggle */}
              {structures.length >= 2 && ['stocks', 'equipment', 'maintenance', 'workOrders', 'milk', 'medications', 'water', 'orders', 'hr', 'hr-dossiers', 'hr-dashboard', 'hr-transport-collection', 'tasks', 'timesheets', 'creche', 'treasury', 'financial', 'diapers'].includes(currentView) && (
                <button
                  onClick={() => setShowAllStructures(!showAllStructures)}
                  className={`hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full border transition text-sm font-medium ${
                    showAllStructures
                      ? 'bg-accent text-white border-accent shadow-sm'
                      : 'bg-transparent text-white/70 border-white/30 hover:text-white hover:border-white/60'
                  }`}
                  title={showAllStructures ? 'Afficher une structure' : 'Afficher toutes les structures'}
                >
                  {showAllStructures ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                  <span>Toutes</span>
                </button>
              )}

              {/* VEGA — Agent proactif (admin only) */}
              {isAdmin && <VegaButton onClick={() => setVegaOpen(true)} summary={vegaSummary} />}

              {/* Stock alerts */}
              <StockAlerts />

              {/* User info - desktop only */}
              <div className="hidden xl:flex items-center gap-1 text-sm">
                <UserCircle size={18} />
                <span className="truncate max-w-[120px]">{user?.name}</span>
              </div>

              {/* Changelog */}
              {onShowChangelog && (
                <button
                  onClick={onShowChangelog}
                  className="relative p-2 hover:bg-primary-dark rounded transition"
                  title="Historique des versions"
                >
                  <FileText size={18} />
                </button>
              )}

              {/* Settings */}
              {onShowSettings && (
                <button
                  onClick={onShowSettings}
                  className="p-2 hover:bg-primary-dark rounded transition"
                  title="Paramètres"
                >
                  <Settings size={18} />
                </button>
              )}

              {/* Logout */}
              <button
                onClick={onLogout}
                className="flex items-center gap-1 p-2 hover:bg-primary-dark rounded transition"
                title="Déconnexion"
              >
                <LogOut size={18} />
                <span className="hidden xl:inline text-sm">Déconnexion</span>
              </button>
            </div>
          </div>

          {/* Mobile: structure selector row */}
          <div className="sm:hidden px-4 pb-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <StructureSelector />
              </div>
              {structures.length >= 2 && ['stocks', 'equipment', 'maintenance', 'workOrders', 'milk', 'medications', 'water', 'orders', 'hr', 'hr-dossiers', 'hr-dashboard', 'hr-transport-collection', 'tasks', 'timesheets', 'creche', 'treasury', 'financial', 'diapers'].includes(currentView) && (
                <button
                  onClick={() => setShowAllStructures(!showAllStructures)}
                  className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full border transition text-xs font-medium ${
                    showAllStructures
                      ? 'bg-accent text-white border-accent shadow-sm'
                      : 'bg-transparent text-white/70 border-white/30'
                  }`}
                >
                  {showAllStructures ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  <span>Toutes</span>
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Barre "Opéré par" — visible sur tous les modules sauf admin */}
        {currentView !== 'admin' && (
          <div className="bg-white border-b border-gray-100 px-4 py-1.5 flex-shrink-0">
            <div className="flex items-center gap-2">
              <User size={13} className="text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-500 flex-shrink-0">Opéré par :</span>
              {people.length > 0 ? (
                <select
                  className="border border-gray-200 rounded px-2 py-0.5 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-white"
                  value={currentPerson?.id || ''}
                  onChange={(e) => {
                    const selected = people.find(p => p.id === e.target.value);
                    if (selected) setCurrentPerson(selected);
                  }}
                >
                  {people.map(p => (
                    <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                  ))}
                </select>
              ) : (
                <span className="text-xs text-gray-400 italic">Aucune personne configurée</span>
              )}
            </div>
          </div>
        )}

        {/* Content area */}
        <main className="flex-1 overflow-y-auto">
          {typeof children === 'function'
            ? children({ showAllStructures, setShowAllStructures })
            : children}
        </main>
      </div>

      {/* VEGA — Panneau insights (admin only) */}
      {isAdmin && (
        <VegaPanel
          isOpen={vegaOpen}
          onClose={() => { setVegaOpen(false); refreshVega(); }}
          onNavigate={onNavigate}
          isAdmin={isAdmin}
        />
      )}

      {/* Nova — Dialog modal (visible si Nova activé) */}
      {showNova && <NovaDialog currentView={currentView} userRole={user?.role} />}

      {/* Recherche globale Ctrl+K */}
      <GlobalSearchModal
        open={globalSearchOpen}
        onClose={() => setGlobalSearchOpen(false)}
        onNavigate={handleGlobalSearchNavigate}
      />
    </div>
    </NovaProvider>
  );
};

export default AppLayout;
